import { useCallback } from "react";

import {
	type EmailChannelWriteSchema,
	type EmailNotificationChannel,
	type EmailNotificationStatus,
	type NotificationChannel,
	type NotificationConfigurationReadSchema,
	type NotificationConfigurationWriteSchema,
	type OpsgenieChannelWriteSchema,
	type OpsgenieNotificationChannel,
	type OpsgenieRegion,
	type OpsgenieResponder,
	type SlackChannel,
	type SlackChannelsChunk,
	type SlackNotificationChannel,
	type TeamsChannelWriteSchema,
	type TeamsNotificationChannel,
	type UpdateNotificationConfigurationParams,
	type WebhookChannelWriteSchema,
	type WebhookNotificationChannel,
	createEmailNotificationChannelFn,
	createNotificationConfigurationFn,
	createOpsgenieNotificationChannelFn,
	createTeamsNotificationChannelFn,
	createWebhookNotificationChannelFn,
	deleteNotificationConfigurationFn,
	getEmailNotificationStatusFn,
	getNotificationConfigurationChannelsFn,
	getNotificationConfigurationFn,
	getNotificationConfigurationsFn,
	getSlackNotificationChannelFn,
	joinSlackChannelFn,
	listOpsgenieRespondersFn,
	listSlackChannelsFn,
	slackChannelDetailsFn,
	testEmailChannelFn,
	testOpsgenieChannelFn,
	testSlackChannelFn,
	testTeamsChannelFn,
	testWebhookChannelFn,
	updateNotificationConfigurationFn,
} from "../common/openapi";

import {
	type UseMutationOptions,
	type UseMutationResult,
	type UseQueryOptions,
	type UseQueryResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "./react-query";

// manage notification channels
export const useNotificationConfiguration = <TResult = NotificationConfigurationReadSchema>(
	notificationConfigurationIdOrUrn: string,
	options?: UseQueryOptions<NotificationConfigurationReadSchema, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["notification", "configuration", notificationConfigurationIdOrUrn],
		() => getNotificationConfigurationFn({ notificationConfigurationIdOrUrn }),
		options,
	);

export const useCreateNotificationConfiguration = (
	options?: UseMutationOptions<NotificationConfigurationReadSchema, Error, NotificationConfigurationWriteSchema>,
): UseMutationResult<NotificationConfigurationReadSchema, Error, NotificationConfigurationWriteSchema> =>
	useMutation((configuration) => createNotificationConfigurationFn({ body: configuration }), options);

export const useUpdateNotificationConfiguration = (
	options?: UseMutationOptions<NotificationConfigurationReadSchema, Error, UpdateNotificationConfigurationParams>,
): UseMutationResult<NotificationConfigurationReadSchema, Error, UpdateNotificationConfigurationParams> =>
	useMutation((configuration) => updateNotificationConfigurationFn(configuration), options);

export const useDeleteNotificationConfiguration = (
	options?: UseMutationOptions<void, Error, string>,
): UseMutationResult<void, Error, string> =>
	useMutation(
		(notificationConfigurationIdOrUrn) => deleteNotificationConfigurationFn({ notificationConfigurationIdOrUrn }),
		options,
	);

export const useNotificationChannels = <TResult = Array<NotificationChannel>>(
	notificationConfigurationIdOrUrn: string,
	options?: UseQueryOptions<Array<NotificationChannel>, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["notification", "channels", notificationConfigurationIdOrUrn],
		() => getNotificationConfigurationChannelsFn({ notificationConfigurationIdOrUrn }),
		options,
	);

export const useNotifications = <TResult = Array<NotificationConfigurationReadSchema>>(
	options?: UseQueryOptions<Array<NotificationConfigurationReadSchema>, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(["notification", "list"], () => getNotificationConfigurationsFn({}), options);

export const useInvalidateNotificationsQuery = () => {
	const queryClient = useQueryClient();

	return useCallback(() => queryClient.invalidateQueries([{}, "notification", "list"]), [queryClient]);
};

// manage slack notification channel
export const useNotificationSlackChannel = <TResult = SlackNotificationChannel>(
	slackNotificationChannelId: number,
	options?: UseQueryOptions<SlackNotificationChannel, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["notification", "slack", "channel", slackNotificationChannelId],
		() => getSlackNotificationChannelFn({ channelId: slackNotificationChannelId }),
		options,
	);

export const useListSlackChannels = <TResult = SlackChannelsChunk>(
	channelId: number,
	options?: UseQueryOptions<SlackChannelsChunk, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(["notification", "slack", "channels", "list", channelId], () => listSlackChannelsFn({ channelId }), options);

export const useSlackChannelDetails = <TResult = SlackChannel>(
	channelId: number,
	slackChannelId: string,
	options?: UseQueryOptions<SlackChannel, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["notification", "slack", "channels", "details", channelId, slackChannelId],
		() => slackChannelDetailsFn({ channelId, slackChannelId }),
		options,
	);

export const useJoinSlackChannelToSlackNotification = (
	options?: UseMutationOptions<SlackNotificationChannel, Error, { channelId: number; slackChannelId: string }>,
): UseMutationResult<SlackNotificationChannel, Error, { channelId: number; slackChannelId: string }> =>
	useMutation(
		({ channelId, slackChannelId }) => joinSlackChannelFn({ channelId, body: { id: slackChannelId } }),
		options,
	);

export const useTestSlackNotificationChannel = (
	options?: UseMutationOptions<void, Error, number>,
): UseMutationResult<void, Error, number> =>
	useMutation((slackNotificationChannelId) => testSlackChannelFn({ channelId: slackNotificationChannelId }), options);

// manage webhook notification channel
export const useCreateWebhookChannel = (
	options?: UseMutationOptions<WebhookNotificationChannel, Error, WebhookChannelWriteSchema>,
): UseMutationResult<WebhookNotificationChannel, Error, WebhookChannelWriteSchema> =>
	useMutation((webhookChannel) => createWebhookNotificationChannelFn({ body: webhookChannel }), options);

export const useTestWebhookNotificationChannel = (
	options?: UseMutationOptions<void, Error, number>,
): UseMutationResult<void, Error, number> =>
	useMutation(
		(webhookNotificationChannelId) => testWebhookChannelFn({ channelId: webhookNotificationChannelId }),
		options,
	);

// manage webhook notification channel
export const useCreateOpsgenieChannel = (
	options?: UseMutationOptions<OpsgenieNotificationChannel, Error, OpsgenieChannelWriteSchema>,
): UseMutationResult<OpsgenieNotificationChannel, Error, OpsgenieChannelWriteSchema> =>
	useMutation((opsgenieChannel) => createOpsgenieNotificationChannelFn({ body: opsgenieChannel }), options);

export const useTestOpsgenieNotificationChannel = (
	options?: UseMutationOptions<void, Error, number>,
): UseMutationResult<void, Error, number> =>
	useMutation(
		(opsgenieNotificationChannelId) => testOpsgenieChannelFn({ channelId: opsgenieNotificationChannelId }),
		options,
	);

export const useListOpsgenieResponders = <TResult = Array<OpsgenieResponder>>(
	region: OpsgenieRegion,
	genieKey: string,
	options?: UseQueryOptions<Array<OpsgenieResponder>, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["notification", "opsgenie", "responders", region, genieKey],
		() => listOpsgenieRespondersFn({ region, genieKey }),
		options,
	);

// manage teams notification channel
export const useCreateTeamsChannel = (
	options?: UseMutationOptions<TeamsNotificationChannel, Error, TeamsChannelWriteSchema>,
): UseMutationResult<TeamsNotificationChannel, Error, TeamsChannelWriteSchema> =>
	useMutation((teamsChannel) => createTeamsNotificationChannelFn({ body: teamsChannel }), options);

export const useTestTeamsNotificationChannel = (
	options?: UseMutationOptions<void, Error, number>,
): UseMutationResult<void, Error, number> =>
	useMutation((teamsNotificationChannelId) => testTeamsChannelFn({ channelId: teamsNotificationChannelId }), options);

// manage e-mail notification channel
export const useCreateEmailChannel = (
	options?: UseMutationOptions<EmailNotificationChannel, Error, EmailChannelWriteSchema>,
): UseMutationResult<EmailNotificationChannel, Error, EmailChannelWriteSchema> =>
	useMutation((emailChannel) => createEmailNotificationChannelFn({ body: emailChannel }), options);

export const useTestEmailNotificationChannel = (
	options?: UseMutationOptions<void, Error, number>,
): UseMutationResult<void, Error, number> =>
	useMutation((emailNotificationChannelId) => testEmailChannelFn({ channelId: emailNotificationChannelId }), options);

export const useEmailNotificationStatus = (
	options?: UseQueryOptions<EmailNotificationStatus, Error, EmailNotificationStatus>,
): UseQueryResult<EmailNotificationStatus, Error> => useQuery([], () => getEmailNotificationStatusFn({}), options);
